import {
  css,
  CSSResult,
  LitElement,
  nothing,
  PropertyValues,
  TemplateResult,
} from "lit";
import {
  installLitIframeCloneGuard,
  isForeignDocumentClone,
} from "./litIframeClone";
import { MessageModal, type MessageModalOptions } from "./modal/MessageModal";
import { TouchDragManager } from "../../element/TouchDragManager";

export abstract class BaseElement extends LitElement {
  static {
    // Keep the html2canvas iframe guard alive under moduleSideEffects:false.
    installLitIframeCloneGuard();
  }

  protected useShadowRoot: boolean = false;
  /**
   * Designer'ın mobil sürükle-bırak yöneticisi (mobiledragover/mobiledrop).
   * Drag-drop kullanmayan elemanlar (ör. viewer UI) için kapatılmalı — yoksa
   * her render'da document'e non-passive touchmove listener eklenip birikir.
   */
  protected useTouchDrag: boolean = true;

  protected abstract renderHtml(): TemplateResult;
  protected parentRoot?: BaseElement;

  protected renderCss(): CSSResult | CSSResult[] {
    return css``;
  }

  firstUpdated(e: any): void {
    super.firstUpdated(e);
  }

  connectedCallback(): void {
    if (isForeignDocumentClone(this)) return;
    super.connectedCallback();
    this.parentRoot = this.findParentRoot();
    if (!this.useShadowRoot) {
      this.forwardStylesToParent();
    }
  }

  //touch settings
  private touchDragManager: TouchDragManager | undefined;
  childUpdated(child: BaseElement): void {
    if (!this.useShadowRoot || !this.useTouchDrag) return;
    // tek sefer yarat (her render'da yeniden yaratıp document listener'ı
    // çoğaltma) — leak'i önler.
    if (!this.touchDragManager) {
      this.touchDragManager = new TouchDragManager(this.shadowRoot!);
    }
    this.touchDragManager.bindEvents(child);
  }

  childDisconnected(child: BaseElement): void {
    if (this.useShadowRoot) {
      this.touchDragManager?.disconnectEvent(child);
    }
  }

  protected findParentRoot(): BaseElement | undefined {
    let parentNode = this.getRootNode();
    if (parentNode instanceof ShadowRoot) return parentNode.host as BaseElement;

    return undefined;
  }

  protected getHostParent(): HTMLElement | undefined {
    if (this.parentElement) return this.parentElement;
    const root = this.getRootNode();
    if ((root as ShadowRoot).host) {
      return (root as ShadowRoot).host as HTMLElement;
    }
  }

  /***styles ****/

  private _childStyle?: HTMLStyleElement;
  private _addedStyles = new Set<string>();
  private _styleAddedListener?: (event: Event) => void;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    if (this.useShadowRoot) {
      // html2canvas iframe'i ayrı Document; constructed stylesheet paylaşılamaz.
      const root = isForeignDocumentClone(this)
        ? this.attachShadow({ mode: "open" })
        : super.createRenderRoot();
      this.parentRoot = undefined;
      // Lit static.styles zaten adoptedStylesheet / <style> ile eklenir.
      // Yalnızca light-DOM çocuklardan gelen style-added olaylarını topla.
      this.ensureStyleAddedListener();
      return root;
    }

    // Light DOM: stilleri üst shadow host'a ilet (Lit yine host'a yazar).
    queueMicrotask(() => this.forwardStylesToParent());

    return this;
  }

  private forwardStylesToParent() {
    const stylesText = this.constructorStylesText();
    if (!stylesText || !this.isConnected) return;
    this.dispatchEvent(
      new CustomEvent("style-added", {
        detail: { styles: stylesText },
        bubbles: true,
        composed: true,
      })
    );
  }

  private constructorStylesText(): string {
    return this.stylesToText(
      (this.constructor as { styles?: unknown }).styles
    );
  }

  private stylesToText(styles: unknown): string {
    if (!styles) return "";
    if (Array.isArray(styles)) {
      return styles.map((style) => String(style)).join("\n");
    }
    return String(styles);
  }

  private ensureStyleAddedListener() {
    if (!this.useShadowRoot || this._styleAddedListener) return;
    this._styleAddedListener = (event: Event) => this.onStyleAdded(event);
    this.addEventListener("style-added", this._styleAddedListener);
  }

  private ensureChildStyleElement() {
    if (!this._childStyle) {
      this._childStyle = document.createElement("style");
      this._childStyle.id = "child-styles";
    }
    if (this._childStyle.parentNode) return;
    if (this.shadowRoot) {
      this.shadowRoot.appendChild(this._childStyle);
    } else {
      this.append(this._childStyle);
    }
  }

  protected onStyleAdded(event: Event) {
    if (!(event instanceof CustomEvent) || !this.useShadowRoot) return;

    const detail = event.detail as { styles?: unknown } | undefined;
    const stylesText =
      typeof detail?.styles === "string"
        ? detail.styles
        : this.stylesToText(detail?.styles);
    if (!stylesText || this._addedStyles.has(stylesText)) return;

    this.ensureChildStyleElement();
    this._childStyle!.textContent += stylesText;
    this._addedStyles.add(stylesText);
  }

  updateContext(context: any, propery: string) {}

  /**messgage ***/
  protected async popModal(
    content: any,
    options: MessageModalOptions = {
      footer: nothing,
      header: nothing,
    }
  ): Promise<any> {
    if (this.shadowRoot) {
      const messageModal = document.createElement(
        "message-modal"
      ) as MessageModal;
      this.shadowRoot?.appendChild(messageModal);

      const response = await messageModal.show(content, options);
      messageModal.remove();
      return response;
    } else {
      this.findParentRoot();
      return this.parentRoot?.popModal(content, options);
    }
  }

  disconnectedCallback(): void {
    if (this._styleAddedListener) {
      this.removeEventListener("style-added", this._styleAddedListener);
      this._styleAddedListener = undefined;
    }
    super.disconnectedCallback();
    this.parentRoot?.childDisconnected(this);
  }

  protected updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);
    if (this._renderUpdate) {
      this._renderUpdate = false;
      this.parentRoot?.childUpdated(this);
    }
  }

  private _renderUpdate: boolean = false;
  protected render(): TemplateResult {
    this._renderUpdate = true;
    return this.renderHtml();
  }
}
